// The account socket client: receives the signed-in user's account messages.
// It retries for as long as the sign-in lasts, and stops only on a no-session
// close, after which the caller re-checks the session.

import {
  ACCOUNT_SOCKET_NO_SESSION,
  AccountServerMessage,
  AccountServerMessageType,
  isAccountServerMessage,
} from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';
import { getAccountWebSocketUrl } from '@airhead-games/shared/utilities/getUrl';
import { EventRelay } from './eventRelay';
import {
  ReconnectingSocket,
  ReconnectingSocketEvents,
} from './reconnectingSocket';
import type { StreamKind } from '../../services/SimpleEventStream';

const logger = createLogger('accountSocketClient');

const DEBUG_KIND: Record<AccountServerMessageType, StreamKind> = {
  [AccountServerMessageType.SNAPSHOT]: 'state_sync',
  [AccountServerMessageType.LOON_BALANCE]: 'state',
  [AccountServerMessageType.ACHIEVEMENT_PROGRESS]: 'event',
  [AccountServerMessageType.POPPY_CHAT_LINK_BLOCKED]: 'event',
};

export interface AccountSocketClientEvents {
  message: AccountServerMessage;
  connectionState: ReconnectingSocketEvents['connectionState'];
}

export class AccountSocketClient {
  private socket = new ReconnectingSocket({
    channel: 'account',
    maxAttempts: Infinity,
    // 1s, 2s, 4s … capped at 30s
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    shouldRetry: (code) => code !== ACCOUNT_SOCKET_NO_SESSION,
  });
  private emitter = new EventRelay<AccountSocketClientEvents>();

  on = this.emitter.on.bind(this.emitter);
  off = this.emitter.off.bind(this.emitter);

  constructor() {
    this.socket.on('connectionState', (payload) =>
      this.emitter.emit('connectionState', payload)
    );
    this.socket.on('message', (message) => {
      if (!isAccountServerMessage(message)) {
        logger.warn('Ignoring unrecognized account message', message);
        return;
      }
      this.socket.pushDebugStream({
        direction: 'in',
        kind: DEBUG_KIND[message.type],
        type: message.type,
        raw: message.payload,
      });
      this.emitter.emit('message', message);
    });
  }

  connect() {
    this.socket.connect(getAccountWebSocketUrl());
  }

  disconnect() {
    this.socket.disconnect();
  }
}
